// 復旧方針生成API
import { apiClient } from './api';

export interface RestorePolicyGenerationRequest {
  company_name: string;
  business_type: string;
  location: string;
  main_business?: string;
  employee_count?: number;
  annual_revenue?: string;
  suppliers?: string[];
  customers?: string[];
  tenant_id?: string;
  search_query?: string;
  include_hazard_analysis?: boolean;
  max_search_results?: number;
  use_parallel_generation?: boolean;
  use_section_based_generation?: boolean;
  extracted_info?: Record<string, any>;
}

export interface RestorePolicyResponse {
  restore_policy_document: string;
  generation_metadata: {
    model: string;
    generation_method: string;
    total_generation_time_seconds: number;
    usage: Record<string, number>;
    phase_metadata?: any[];
  };
  company_results_used: any[];
  template_results_used: any[];
  hazard_info?: any;
  company_info: Record<string, any>;
  financial_info?: Record<string, any>;
}

// 非同期ジョブ関連の型定義
export type RestorePolicyJobStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface RestorePolicyJobResponse {
  job_id: string;
  status: RestorePolicyJobStatus;
  result?: RestorePolicyResponse;
  error?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

export const generateRestorePolicy = async (
  request: RestorePolicyGenerationRequest
): Promise<RestorePolicyResponse> => {
  try {
    const response = await apiClient.postRaw<RestorePolicyResponse>(
      '/api/restore-policy/generate',
      request,
      {
        timeout: 300000, // 5分のタイムアウト
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('復旧方針生成エラー:', error);
    throw new Error(
      error.response?.data?.detail ||
      '復旧方針の生成中にエラーが発生しました'
    );
  }
};

// 非同期ジョブAPI
export const generateRestorePolicyAsync = async (
  request: RestorePolicyGenerationRequest
): Promise<{ job_id: string }> => {
  try {
    const response = await apiClient.postRaw<{ job_id: string }>(
      '/api/restore-policy/generate-async',
      request,
      {
        timeout: 10000, // 10秒のタイムアウト(即座に返却されるため)
      }
    );

    console.log('=== generateRestorePolicyAsync Response Debug ===');
    console.log('Response status:', response.status);
    console.log('Response data:', response.data);
    console.log('Response data keys:', Object.keys(response.data || {}));
    console.log('job_id value:', response.data?.job_id);
    console.log('job_id type:', typeof response.data?.job_id);
    console.log('=== END Debug ===');

    return response.data;
  } catch (error: any) {
    console.error('非同期ジョブ作成エラー:', error);
    throw new Error(
      error.response?.data?.detail ||
      '非同期ジョブの作成中にエラーが発生しました'
    );
  }
};

export const getRestorePolicyJobStatus = async (
  jobId: string,
  tenantId?: string
): Promise<RestorePolicyJobResponse> => {
  try {
    const params = new URLSearchParams();
    if (tenantId) {
      params.append('tenant_id', tenantId);
    }

    const url = `/api/restore-policy/jobs/${jobId}${
      params.toString() ? `?${params.toString()}` : ''
    }`;

    const response = await apiClient.getRaw<RestorePolicyJobResponse>(
      url,
      {
        timeout: 60000, // 60秒のタイムアウト
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('ジョブステータス取得エラー:', error);
    throw new Error(
      error.response?.data?.detail ||
      'ジョブステータスの取得中にエラーが発生しました'
    );
  }
};

// 生成された復旧方針をパース
export const parseRestorePolicy = (document: string) => {
  console.log('=== parseRestorePolicy DEBUG ===');
  console.log('Document length:', document.length);
  console.log('Document preview:', document.substring(0, 500));

  const sections: Record<string, string> = {};

  // セクションごとに分割（11セクション対応: 1-2と1-3を統合）
  const sectionPatterns = [
    { id: 'company_info', pattern: /##?\s*企業の基本情報[\s\S]*?(?=##|$)/i },
    { id: 'disaster_risks', pattern: /##?\s*1-1\s*想定されるリスク[\s\S]*?(?=##|$)/i },
    { id: 'important_business', pattern: /##?\s*1-2\s*重要事業と目標復旧時間[\s\S]*?(?=##|$)/i },
    { id: 'human_resources', pattern: /##?\s*2-1\s*重要事業の実施に必要な人的リソース[\s\S]*?(?=##|$)/i },
    { id: 'facility_recovery', pattern: /##?\s*2-2\s*重要事業の実施に必要な設備・インフラ[\s\S]*?(?=##|$)/i },
    { id: 'it_recovery', pattern: /##?\s*2-3\s*重要事業の実施に必要なシステム[\s\S]*?(?=##|$)/i },
    { id: 'supply_chain', pattern: /##?\s*2-4\s*重要事業の実施に必要な委託先／関係先[\s\S]*?(?=##|$)/i },
    { id: 'financial_plan', pattern: /##?\s*2-5\s*事業継続のための財務計画[\s\S]*?(?=##|$)/i },
    { id: 'backup_location', pattern: /##?\s*3-1\s*バックアップ拠点[\s\S]*?(?=##|$)/i },
    { id: 'remote_work', pattern: /##?\s*3-2\s*業務のリモート対応[\s\S]*?(?=##|$)/i },
    { id: 'external_partners', pattern: /##?\s*3-3\s*外部業者・パートナー[\s\S]*?(?=##|$)/i },
    { id: 'inventory_utilization', pattern: /##?\s*3-4\s*在庫活用[\s\S]*?(?=##|$)/i },
  ];

  sectionPatterns.forEach(({ id, pattern }) => {
    const match = document.match(pattern);
    console.log(`Section ${id}:`, match ? `matched (${match[0].length} chars)` : 'NOT matched');
    if (match) {
      // セクションタイトルを除去してコンテンツのみ取得
      let content = match[0];
      content = content.replace(/##?\s*[0-9-]*\s*[^\n]+\n/, '').trim();
      sections[id] = content;
    }
  });

  console.log('Parsed sections:', Object.keys(sections));
  console.log('=== END DEBUG ===');

  return sections;
};
